import type { Tone } from '../../components/ui/index.js';

/**
 * How a sample's acceptance state is coloured.
 *
 * WRITTEN ONCE, USED BY EVERY SAMPLE SCREEN, so the register and the sample
 * file cannot colour the same fact differently — a reader scanning a column of
 * badges reads the colour before the word.
 *
 * `accepted_with_notes` is deliberately NOT the same green as `accepted`. The
 * notes exist because somebody thought the material was worth a remark, and
 * flattening the two would throw that remark away at exactly the moment a
 * technician is deciding whether to start work (CLAUDE.md section 8).
 *
 * `awaiting_inspection` is the WAITING tone (D278), not a warning: nobody has
 * decided yet, "undecided" is not "a problem", and the decision is another
 * person's act — the inspector's — so the register marks it as waiting on them.
 */
export function toneForSampleStatus(status: string): Tone {
  if (status === 'accepted') return 'success';
  if (status === 'accepted_with_notes') return 'info';
  if (status === 'held') return 'warning';
  if (status === 'rejected') return 'danger';
  if (status === 'awaiting_inspection') return 'waiting';
  return 'neutral';
}

/**
 * How one recorded custody movement is coloured.
 *
 * A `correction` is `warning`, not neutral: it is the record saying an earlier
 * link in the chain was wrong, and it must not read as an ordinary move. A
 * `disposed` movement is the end of the physical material, so it is `danger` —
 * not because anything went wrong, but because nothing can be re-tested after
 * it.
 */
export function toneForMovementType(movementType: string): Tone {
  if (movementType === 'correction') return 'warning';
  if (movementType === 'disposed') return 'danger';
  if (movementType === 'returned_to_client') return 'info';
  return 'neutral';
}
